import { useEffect, useCallback } from 'react';
import { VibesitesImage } from '@vibesites/ui-primitives';

export interface LightboxImage {
  url: string;
  alt: string;
}

interface LightboxProps {
  images: LightboxImage[];
  selectedIndex: number | null;
  onClose: () => void;
  onSelect: (index: number) => void;
}

/**
 * Shared lightbox overlay — used by Gallery and PropertyOverview.
 * Renders nothing when `selectedIndex` is null.
 * Supports keyboard navigation (←/→/Escape) and click-outside-to-close.
 */
export function Lightbox({ images, selectedIndex, onClose, onSelect }: LightboxProps) {
  const navigate = useCallback(
    (direction: 1 | -1) => {
      if (selectedIndex === null || images.length === 0) return;
      onSelect((selectedIndex + direction + images.length) % images.length);
    },
    [selectedIndex, images.length, onSelect],
  );

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedIndex, navigate, onClose]);

  if (selectedIndex === null) return null;
  const selected = images[selectedIndex];
  if (!selected) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors duration-200"
        aria-label="Close lightbox"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Prev arrow */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); navigate(-1); }}
          className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] hover:bg-white transition-all duration-200"
          aria-label="Previous image"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
        </button>
      )}

      <div className="w-full h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <VibesitesImage
          url={selected.url}
          alt={selected.alt}
          sizes="100vw"
          className="w-full h-full object-contain"
        />
      </div>

      {/* Next arrow */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); navigate(1); }}
          className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] hover:bg-white transition-all duration-200"
          aria-label="Next image"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default Lightbox;
